import * as React from 'react';
import { t } from '@reos/i18n';
import { Pencil, TriangleAlert } from 'lucide-react';
import { V4Group, V4Segmented, V4State } from '../ux-v4.js';
import { PriceExpectationNote } from '../price-expectation.js';
import { focusField } from '../../lib/form-errors.js';
import { useLeaveWarning } from '../../lib/leave-guard.js';
import { priceExpectationPrompt } from '../../lib/owner-price-expectation.js';
import type { DraftFieldView, DraftPatch, ListingDraftView } from '../../lib/listing-draft-api.js';
import {
  checkState,
  checkText,
  editorText,
  fieldLabel,
  formatFloor,
  formatValue,
  kindLabel,
  originLabel,
  originsSentence,
  parseEditorValue,
} from '../../lib/listing-draft-format.js';

/**
 * Krok UXV4b bod 1 — „Vyplnili jsme N údajů" (predloha `Inzerat.png`).
 *
 * Ctyri hlavni udaje, ostatni po rozbaleni; u kazdeho puvod (katastr, RUIAN,
 * PENB, SVJ, „tvrdí majitel"). Kazdy jde prepsat (radek → editor). „Doplň"
 * jen to, co REOS nema (cena, volne od). „Kontrola" = vysledek kontroly
 * proti katastru ze serveru; cenu tu neopakujeme, stoji v „Doplň".
 */

const cs = { locale: 'cs' } as const;

function FieldEditor({
  field,
  onSave,
  onClose,
}: {
  readonly field: DraftFieldView;
  readonly onSave: (value: unknown) => Promise<void>;
  readonly onClose: () => void;
}): React.JSX.Element {
  const [text, setText] = React.useState(() => editorText(field));
  const [error, setError] = React.useState(false);
  /*
   * Krok APP2 bod 4 (bod 25): prepsana hodnota zije JEN TADY, dokud makler
   * nepotvrdi „Uložit" — obnoveni stranky ji zahodi. Hlidka proto bezi presne
   * po tu dobu, kdy je text jiny nez ulozena hodnota (SSOT `lib/leave-guard.ts`).
   */
  useLeaveWarning(text !== editorText(field));
  const save = async (value: unknown): Promise<void> => {
    if (value === undefined) {
      setError(true);
      return;
    }
    await onSave(value);
    onClose();
  };
  let control: React.ReactNode;
  if (field.valueType === 'boolean') {
    control = (
      <V4Segmented
        value={field.value === true ? 'yes' : field.value === false ? 'no' : ''}
        options={[
          { value: 'yes', label: t('listingDraft.value.yes', cs) },
          { value: 'no', label: t('listingDraft.value.no', cs) },
        ]}
        onChange={(next) => void save(next === 'yes')}
        label={fieldLabel(field.key)}
        testId={`ld-edit-${field.key}`}
      />
    );
  } else if (field.options !== null) {
    control = (
      <select
        className="rh-input rh-ld-input"
        value={typeof field.value === 'string' ? field.value : ''}
        aria-label={fieldLabel(field.key)}
        onChange={(event) => void save(event.target.value)}
        data-testid={`ld-edit-${field.key}`}
      >
        <option value="" disabled>
          —
        </option>
        {field.options.map((option) => (
          <option key={option} value={option}>
            {field.valueType === 'energy_class'
              ? option
              : formatValue({ valueType: field.valueType, unit: null, value: option })}
          </option>
        ))}
      </select>
    );
  } else {
    control = (
      <form
        className="rh-ld-editor__form"
        onSubmit={(event) => {
          event.preventDefault();
          void save(parseEditorValue(field, text));
        }}
      >
        <input
          className="rh-input rh-ld-input"
          value={text}
          autoFocus
          inputMode={field.valueType === 'number' || field.valueType === 'integer' ? 'decimal' : 'text'}
          aria-label={fieldLabel(field.key)}
          aria-invalid={error}
          onChange={(event) => {
            setText(event.target.value);
            setError(false);
          }}
          data-testid={`ld-edit-${field.key}`}
        />
        <button type="submit" className="rh-v4-btn rh-v4-btn--chip" data-testid={`ld-edit-save-${field.key}`}>
          {t('webAdmin.listingDraft.data.save', cs)}
        </button>
      </form>
    );
  }
  return (
    <div className="rh-ld-editor" data-testid={`ld-editor-${field.key}`}>
      {control}
      {error ? (
        <p className="rh-ld-error" role="alert">
          {t('webAdmin.listingDraft.error.invalidValue', cs)}
        </p>
      ) : null}
      <div className="rh-ld-editor__acts">
        {field.overridden ? (
          <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={() => void save(null)} data-testid={`ld-reset-${field.key}`}>
            {t('webAdmin.listingDraft.data.reset', cs)}
          </button>
        ) : null}
        <button type="button" className="rh-v4-btn rh-v4-btn--plain" onClick={onClose}>
          {t('webAdmin.listingDraft.data.cancelEdit', cs)}
        </button>
      </div>
    </div>
  );
}

function FieldRow({
  field,
  display,
  open,
  onToggle,
  onSave,
}: {
  readonly field: DraftFieldView;
  readonly display: string | null;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onSave: (value: unknown) => Promise<void>;
}): React.JSX.Element {
  const conflictWith = field.conflict
    ? (field.alternatives.find((alt) => alt.origin !== field.origin) ?? null)
    : null;
  const content = (
    <>
      <span className="rh-ld-row__label">{fieldLabel(field.key)}</span>
      <span className="rh-ld-row__right">
        <span className="rh-ld-row__value" data-testid={`ld-value-${field.key}`}>
          {display ?? (
            <span className="rh-ld-row__empty">
              {t('webAdmin.listingDraft.data.fill', cs)}
              {field.editable ? <Pencil size={13} aria-hidden="true" /> : null}
            </span>
          )}
        </span>
        {field.origin !== null ? (
          <span className={`rh-ld-row__origin${field.official ? '' : ' is-claim'}`} data-testid={`ld-origin-${field.key}`}>
            {originLabel(field.origin)}
          </span>
        ) : null}
        {conflictWith !== null ? (
          <span className="rh-ld-row__conflict" data-testid={`ld-conflict-${field.key}`}>
            <TriangleAlert size={13} aria-hidden="true" />
            {t('webAdmin.listingDraft.data.conflictNote', {
              ...cs,
              values: {
                origin: originLabel(conflictWith.origin),
                value: formatValue({ valueType: field.valueType, unit: field.unit, value: conflictWith.value }) ?? '—',
              },
            })}
          </span>
        ) : null}
      </span>
    </>
  );
  return (
    <div className="rh-ld-row-wrap">
      {field.editable ? (
        <button
          type="button"
          className="rh-ld-row"
          aria-expanded={open}
          aria-label={t('webAdmin.listingDraft.data.editLabel', { ...cs, values: { field: fieldLabel(field.key) } })}
          onClick={onToggle}
          data-testid={`ld-row-${field.key}`}
        >
          {content}
        </button>
      ) : (
        <div className="rh-ld-row" data-testid={`ld-row-${field.key}`}>
          {content}
        </div>
      )}
      {open ? <FieldEditor field={field} onSave={onSave} onClose={onToggle} /> : null}
    </div>
  );
}

function FillRow({
  field,
  placeholder,
  onSave,
}: {
  readonly field: DraftFieldView;
  readonly placeholder: string;
  readonly onSave: (value: unknown) => Promise<void>;
}): React.JSX.Element {
  const [text, setText] = React.useState(() => editorText(field));
  const [error, setError] = React.useState(false);
  React.useEffect(() => setText(editorText(field)), [field]);
  /* Krok APP2 bod 4: „Doplň" uklada az na `blur`/Enter — do te doby je to tady. */
  useLeaveWarning(text !== editorText(field));
  const commit = (): void => {
    if (text === editorText(field)) return;
    const value = parseEditorValue(field, text);
    if (value === undefined) {
      setError(true);
      return;
    }
    setError(false);
    void onSave(value);
  };
  return (
    <label className="rh-ld-fill" data-testid={`ld-fill-${field.key}`}>
      <span className="rh-ld-fill__label">{fieldLabel(field.key)}</span>
      <input
        className="rh-ld-fill__input"
        value={text}
        placeholder={placeholder}
        inputMode={field.valueType === 'money' ? 'numeric' : 'text'}
        aria-invalid={error}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
        data-testid={`ld-input-${field.key}`}
      />
    </label>
  );
}

export function DataStep({
  view,
  onSave,
  /**
   * Krok UXP4b bod 3 (nalez P2-8) — VSECHNY UDAJE VIDET NAJEDNOU. Do UXP4b
   * stala na kroku 1 jen ctverice „hlavnich" udaju a zbytek byl schovany pod
   * „Zobrazit, co chybí"; povinna pole portalu se tak makléř dozvidal po
   * kapkach az na kroku 2 (devatenact bodu v jednom pruchodu). Vychozi stav
   * je proto ROZBALENO; sbalit jde dal („Méně údajů").
   */
  showAllInitially = true,
}: {
  readonly view: ListingDraftView;
  readonly onSave: (patch: DraftPatch) => Promise<void>;
  readonly showAllInitially?: boolean;
}): React.JSX.Element {
  const [all, setAll] = React.useState(showAllInitially);
  const [editing, setEditing] = React.useState<string | null>(null);
  const byKey = new Map(view.fields.map((field) => [field.key, field]));
  const elevator = byKey.get('has_elevator')?.value;
  const display = (field: DraftFieldView): string | null =>
    field.key === 'floor_number' ? formatFloor(field.value, elevator) : formatValue(field);
  const saveField = (key: string) => (value: unknown) => onSave({ overrides: { [key]: value } });

  const main = view.fields.filter((field) => field.main);
  const rest = view.fields.filter((field) => !field.main && field.listed);
  const known = rest.filter((field) => field.value !== null);
  const unknown = rest.filter((field) => field.value === null);
  const sources = originsSentence(view.officialOrigins);
  const checks = view.checks
    .map((check) => ({ check, text: checkText(check, view.propertyKind) }))
    .filter((row): row is { check: (typeof view.checks)[number]; text: string } => row.text !== null);

  /*
   * Krok UXP4b bod 3 (nalez P2-8) — „PLOCHA" A „PODLAHOVÁ PLOCHA" JAKO JEDEN
   * ÚKON. RealityMIX chce u bytu podlahovou plochu zvlast od uzitne a z uzitne
   * se NEDOVOZUJE (ROZHODNUTI O10-5: cizi portal by dostal cislo, ktere nikdo
   * nerekl). Dve pole vedle sebe ale makléř cte jako dve ruzne vymery. Proto
   * zustavaji dve pole v datech a jeden UKON na obrazovce: jedno klepnuti
   * „Stejná jako plocha (54 m²)" zapise hodnotu JAKO ZAPIS MAKLÉŘE — potvrdil
   * ji clovek, nedomyslel si ji REOS.
   */
  const usableArea = byKey.get('usable_area_m2');
  const sameAsUsable =
    usableArea?.value === null || usableArea === undefined
      ? null
      : formatValue(usableArea);

  /**
   * Krok PROP3B bod 1 — PROC JE POLE CENY PRAZDNE. Cislo ze zalozeni zakazky
   * je po PROP3 „predstava majitele"; nabidkovou cenou se stane az tady,
   * klepnutim maklere. Zapis jde TOUTEZ cestou jako rucni vyplneni pole
   * (PATCH konceptu → `setFact` s puvodem „makléř" a casem klepnuti).
   */
  const pricePrompt = (field: DraftFieldView): React.JSX.Element | null => {
    const prompt = priceExpectationPrompt({
      hasAskingPrice: field.value !== null,
      ownerExpectation: view.ownerPriceExpectation,
    });
    return (
      <PriceExpectationNote
        prompt={prompt}
        onUse={() => {
          if (prompt?.kind === 'offer') void saveField('asking_price')(prompt.expectation);
        }}
        onEnterOther={() => focusField('ld-input-asking_price')}
        missingText={t('webAdmin.priceExpectation.missingListing', cs)}
      />
    );
  };

  const row = (field: DraftFieldView): React.JSX.Element => {
    const node = (
      <FieldRow
        key={field.key}
        field={field}
        display={display(field)}
        open={editing === field.key}
        onToggle={() => setEditing((current) => (current === field.key ? null : field.key))}
        onSave={saveField(field.key)}
      />
    );
    if (field.key !== 'floor_area_m2' || field.value !== null || sameAsUsable === null) return node;
    return (
      <React.Fragment key={field.key}>
        {node}
        <button
          type="button"
          className="rh-v4-btn rh-v4-btn--plain rh-ld-same-area"
          onClick={() => void saveField('floor_area_m2')(usableArea?.value)}
          data-testid="ld-floor-area-same"
        >
          {t('webAdmin.listingDraft.data.sameAsUsableArea', { ...cs, values: { area: sameAsUsable } })}
        </button>
      </React.Fragment>
    );
  };

  return (
    <div className="rh-ld-step" data-testid="ld-step-data">
      <div className="rh-ld-hero">
        {view.filledCount > 0 ? (
          <svg className="rh-ld-hero__icon" width="52" height="52" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="11" />
            <path d="M7 12.5l3.2 3.2L17 9" />
          </svg>
        ) : null}
        <h1 className="rh-ld-hero__title" data-testid="ld-filled-title">
          {view.filledCount > 0
            ? t('webAdmin.listingDraft.data.filled', { ...cs, values: { count: view.filledCount } })
            : t('webAdmin.listingDraft.data.needsFilling', cs)}
        </h1>
        <p className="rh-ld-hero__sub">
          {[view.addressText, kindLabel(view.propertyKind)].filter((part) => part !== null).join(' · ')}
          {sources !== null ? (
            <>
              <br />
              {sources}
            </>
          ) : null}
        </p>
        {!view.reviewDone ? (
          <p className="rh-ld-note rh-ld-note--warn" data-testid="ld-no-review">
            {t('webAdmin.listingDraft.data.noReview', cs)}
          </p>
        ) : null}
      </div>

      <V4Group testId="ld-fields">
        {main.map(row)}
        {all ? (
          <>
            {known.map(row)}
            {unknown.length > 0 ? (
              <div className="rh-ld-subhead">{t('webAdmin.listingDraft.data.unknownHeading', cs)}</div>
            ) : null}
            {unknown.map(row)}
          </>
        ) : null}
        <button
          type="button"
          className="rh-ld-more"
          aria-expanded={all}
          onClick={() => setAll((value) => !value)}
          data-testid="ld-toggle-all"
        >
          {all
            ? t('webAdmin.listingDraft.data.showLess', cs)
            : view.filledCount > 0
              ? t('webAdmin.listingDraft.data.showAll', { ...cs, values: { count: view.filledCount } })
              : t('webAdmin.listingDraft.data.showMissing', cs)}
        </button>
      </V4Group>
      <p className="rh-ld-foot">{t('webAdmin.listingDraft.data.note', cs)}</p>

      <h2 className="rh-ld-h">{t('webAdmin.listingDraft.data.fill', cs)}</h2>
      <V4Group testId="ld-fill">
        {view.fill.map((field) => (
          <React.Fragment key={field.key}>
            <FillRow
              field={field}
              placeholder={t(
                field.key === 'asking_price'
                  ? 'webAdmin.listingDraft.data.pricePlaceholder'
                  : 'webAdmin.listingDraft.data.availablePlaceholder',
                cs,
              )}
              onSave={saveField(field.key)}
            />
            {field.key === 'asking_price' ? pricePrompt(field) : null}
          </React.Fragment>
        ))}
      </V4Group>

      {checks.length > 0 ? (
        <>
          <h2 className="rh-ld-h rh-ld-h--gap">{t('webAdmin.listingDraft.data.check', cs)}</h2>
          <V4Group testId="ld-checks">
            {checks.map(({ check, text }) => (
              <div key={check.code} className="rh-ld-check" data-testid={`ld-check-${check.code}`}>
                <V4State state={checkState(check)} />
                <span>{text}</span>
              </div>
            ))}
          </V4Group>
        </>
      ) : null}
    </div>
  );
}
